/**
 * computer-interaction-focus
 *
 * Browser helpers that keep live computer surfaces from losing focus to the
 * chat composer.
 *
 * Primary responsibilities:
 * - Mark live browser/preview controls as active computer interaction targets
 * - Track recent computer interaction across iframe focus boundaries
 * - Let composer focus requests back off while takeover is active
 *
 * Dependencies:
 * - Browser DOM APIs
 */

export const COMPUTER_INTERACTION_ATTRIBUTE = 'data-losi-computer-interaction'

const COMPUTER_INTERACTION_HOLD_MS = 15_000
const COMPUTER_INTERACTION_SELECTOR = `[${COMPUTER_INTERACTION_ATTRIBUTE}="true"]`

declare global {
  interface Window {
    __losiComputerInteractionUntil?: number
  }
}

// --------------

function getElementFromTarget(target: EventTarget | Node | null): Element | null {
  if (!target) return null
  if (target instanceof Element) return target
  if (target instanceof Node) return target.parentElement

  return null
}

// --------------

export function markComputerInteraction() {
  if (typeof window === 'undefined') return

  window.__losiComputerInteractionUntil = Date.now() + COMPUTER_INTERACTION_HOLD_MS
}

export function isComputerInteractionTarget(target?: EventTarget | Node | null) {
  return Boolean(
    getElementFromTarget(target ?? null)?.closest(COMPUTER_INTERACTION_SELECTOR)
  )
}

export function shouldPreserveComputerInteractionFocus(
  target?: EventTarget | Node | null
) {
  if (typeof document === 'undefined') return false

  if (isComputerInteractionTarget(target)) return true
  if (isComputerInteractionTarget(document.activeElement)) return true

  return (window.__losiComputerInteractionUntil ?? 0) > Date.now()
}
